import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, Optional, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentResult, AssessmentSettings, Course, Learnspace, LearnspaceSessionsResponse, LearnspaceSessionsStats } from '@shared/models';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { getAssessmentDisplayParams } from '@shared/utils/assessment.utils';
import { getRoomUrl } from '@shared/utils/url-builder.utils';
import { environment } from '@env/environment';
import { InternalAnalyticsService } from '@shared/services/internalAnalytics.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { GenericConfirmationDialogComponent } from '@shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

@Component({
  selector: 'program-launcher',
  templateUrl: './program-launcher.component.html',
  styleUrls: ['./program-launcher.component.scss']
})
export class ProgramLauncherComponent implements OnInit, AfterViewInit, OnDestroy {
  public program: Learnspace;
  public simulationList: Course[];
  public nextSimulation?: Course;
  public isDialog: boolean = true;
  public isAnonymous: boolean = true;
  private _sessionByCourse: Map<string, { sessionId: string, xApiSessionId: string, durationMinutes: number, assessmentPreview?: { summary: string, score: string, scoreMax: number, assessmentResult: AssessmentResult } }> = new Map();
  private _stats: LearnspaceSessionsStats = { totalDuration: 0, totalSessions: 0, averageScore: null, averageMaxScore: 5 };
  private _resizeSub: Subscription;
  public activeReport: { courseId: string, sessionId: string, xApiSessionId: string, assessmentResult: AssessmentResult } | null = null;
  private _excludeSessionId: string;
  private _currentCourseId: string;
  private _roomUrlRoot: string;

  @ViewChildren('list') listElement!: QueryList<ElementRef>;

  constructor(
    private _courseService: CoursesAPIService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _internalAnalyticsService: InternalAnalyticsService,
    private _authService: AuthenticationService,
    private _cdr: ChangeDetectorRef,
    private _dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<ProgramLauncherComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private _dialogData: { learnspace: Learnspace }
  ) { }

  async ngOnInit() {
    this.isDialog = !!this.dialogRef;
    if (this.dialogRef) {
      this._updateDialogSize();
      this._resizeSub = fromEvent(window, 'resize').pipe(debounceTime(100)).subscribe(() => this._updateDialogSize());
    }
    const programId = this._route.snapshot.paramMap.get('programId');
    if (this._dialogData?.learnspace) {
      this.program = this._dialogData.learnspace;
      this._router.navigate([], { queryParams: { pl: this.program.id }, queryParamsHandling: 'merge' });
      this.dialogRef.afterClosed().subscribe(() => {
        this._router.navigate([], { queryParams: { pl: null }, queryParamsHandling: 'merge' });
      });
      this._cdr.detectChanges();
    } else {
      if (window.parent) {
        this._internalAnalyticsService.hideChat();
      }
    }
    this.program = await this._courseService.getProgramCourseList(programId || this.program.id).toPromise();
    this._roomUrlRoot = getRoomUrl(this.program.hub?.mode);
    this.simulationList = this.program.courses; //sorted by position in the API service
    await this._loadProgramSession();
  }

  private async _loadProgramSession() {
    this._excludeSessionId = this._route.snapshot.queryParams['session'];
    this._currentCourseId = this._route.snapshot.queryParams['courseId'];
    this.isAnonymous = !(await this._authService.isAuth());
    if (this.isAnonymous) {
      // Anonymous users have no session/stats data — guess done/next from the current course position instead.
      this.nextSimulation = this._getNextSimulationByPosition();
    } else {
      const sessionsResponse = await this._courseService.getLearnspaceSessions(this.program.id, this._excludeSessionId).toPromise();
      this._applySessionsResponse(sessionsResponse);
      this.nextSimulation = this._getNextSimulation();
    }
  }

  private _getNextSimulation(): Course | undefined {
    const lastDoneIndex = this.simulationList.reduce((acc, s, i) => this._sessionByCourse.has(s.id) ? i : acc, -1);
    return this.simulationList[lastDoneIndex + 1];
  }

  private _getNextSimulationByPosition(): Course | undefined {
    const currentCourse = this.simulationList.find(s => s.id === this._currentCourseId);
    if (!currentCourse) return this.simulationList[0];
    return this.simulationList.find(s => s.position > currentCourse.position);
  }

  private _isDoneByPosition(simulation: Course): boolean {
    const currentCourse = this.simulationList.find(s => s.id === this._currentCourseId);
    return !!currentCourse && simulation.position < currentCourse.position;
  }

  private _applySessionsResponse(response: LearnspaceSessionsResponse) {
    this._stats = response.stats;
    this._sessionByCourse = new Map(
      response.sessions.map(s => {
        let assessmentPreview: { summary: string, score: string, scoreMax: number, assessmentResult: AssessmentResult };
        if (s.assessment) {
          const assessmentResult = s.assessment as unknown as AssessmentResult;
          const assessmentSettings = s.assessment.criteria as AssessmentSettings;
          const { score, scoreMax } = getAssessmentDisplayParams(assessmentResult, assessmentSettings);
          assessmentPreview = { summary: assessmentResult.report.evaluationSummary, score, scoreMax, assessmentResult };
        }
        const durationMinutes = Math.ceil(s.data.duration / 60);
        return [s.course_id, { sessionId: s.id, xApiSessionId: s.xapi_session_id, durationMinutes, assessmentPreview }];
      })
    );
  }

  getAssessmentPreview(simulation: Course) {
    return this._sessionByCourse.get(simulation.id)?.assessmentPreview;
  }

  getLastSessionDuration(simulation: Course): number | undefined {
    return this._sessionByCourse.get(simulation.id)?.durationMinutes;
  }

  ngAfterViewInit() {
    this.listElement.changes.subscribe(() => {
      if (this.nextSimulation) {
        this.scrollTo(this.nextSimulation.id, 256);
      }
    });
  }

  private scrollTo(elementId: string, delta: number = 0) {
    const el = document.getElementById(elementId);
    if (el) {
      el.parentElement.scrollTop = el.offsetTop - delta;
    }
  }

  openAssessment(courseId: string) {
    const session = this._sessionByCourse.get(courseId);
    this.activeReport = { courseId, sessionId: session.sessionId, xApiSessionId: session.xApiSessionId, assessmentResult: session.assessmentPreview.assessmentResult };
  }

  closeReport() {
    this.activeReport = null;
  }

  get doneCount(): number {
    return this.simulationList?.filter(s => this.isDone(s)).length ?? 0;
  }

  get isStarted(): boolean {
    return this.doneCount > 0;
  }

  get isComplete(): boolean {
    return this.doneCount > 0 && this.doneCount === this.simulationList?.length;
  }

  get totalSessions(): number {
    return this._stats.totalSessions;
  }

  get totalDuration(): number {
    return Math.round(this._stats.totalDuration / 60);
  }

  get averageScore(): string | null {
    return this._stats.averageScore != null ? this._stats.averageScore.toFixed(1) : null;
  }

  get averageScoreMax(): number {
    return this._stats.averageMaxScore;
  }

  get posterUrl(): string {
    return this.program?.postermedia?.path || '/assets/wonda/background2_ld.jpg';
  }

  get ownerDisplayName(): string {
    const owner = this.program?.owner;
    if (!owner) return '';
    const parts = [owner.first_name, owner.last_name].filter(Boolean);
    return parts.length ? parts.join(' ') : (owner.name || owner.email || '');
  }

  get ownerInitials(): string {
    return this.ownerDisplayName
      .split(/\s+/)
      .map(s => s.length ? s[0].toUpperCase() : '')
      .join('')
      .substring(0, 2);
  }

  isDone(simulation: Course): boolean {
    if (this.isAnonymous) {
      return this._isDoneByPosition(simulation);
    }
    return this._sessionByCourse.has(simulation.id);
  }

  isCurrentCourse(simulation: Course): boolean {
    return !!this._currentCourseId && simulation.id === this._currentCourseId;
  }

  isCurrentSession(simulation: Course): boolean {
    return !!this._excludeSessionId && this.isCurrentCourse(simulation);
  }

  isReachable(simulation: Course): boolean {
    if (this.isAnonymous) {
      if (this._currentCourseId) {
        return this._isDoneByPosition(simulation) || this.isCurrentCourse(simulation);
      }
      return simulation.id === this.nextSimulation?.id;
    }

    if (!this.nextSimulation) return true;
    const nextIndex = this.simulationList.findIndex(s => s.id === this.nextSimulation.id);
    const simIndex = this.simulationList.findIndex(s => s.id === simulation.id);
    return simIndex <= nextIndex;
  }

  openCourse(id: string) {
    const url = `${environment.coursesApiUrl}/api/courses/${id}/room`;
    if (this._excludeSessionId && !this.isComplete) {
      this._confirmAndRun(
        { title: 'Leave this page?', message: 'This will navigate you away from this page.', abortText: 'CANCEL', proceedText: 'CONTINUE' },
        () => this.openURL(url)
      );
    } else {
      this.openURL(url);
    }
  }

  restartCourse(id: string) {
    const url = `${environment.coursesApiUrl}/api/courses/${id}/room`;
    if (this._excludeSessionId && !this.isComplete) {
      this._confirmAndRun(
        { title: 'Leave this page?', message: 'This will navigate you away from this page to restart the simulation.', abortText: 'CANCEL', proceedText: 'CONTINUE' },
        () => this.openURL(url)
      );
    } else {
      this.openURL(url);
    }
  }

  closeComponent() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (window.parent !== window) {
      window.parent.postMessage({ event: 'closeProgramLauncher' }, this._roomUrlRoot);
    } else {
      this.openURL(environment.proUrl);
    }
  }

  returnHome() {
    const goHome = () => this.dialogRef ? this.dialogRef.close() : this.openURL(environment.proUrl);  // no because I might not have access ${this.program.hub.urlTitle}`
    if (this._excludeSessionId && !this.isComplete) {
      this._confirmAndRun(
        { title: 'Leave current simulation?', message: 'Your current progress won\'t be saved', abortText: 'CANCEL', proceedText: 'LEAVE' },
        goHome
      );
    } else {
      goHome();
    }
  }

  private _confirmAndRun(data: { title: string, message: string, abortText: string, proceedText: string }, action: () => void) {
    this._dialog.open(GenericConfirmationDialogComponent, {
      width: '450px',
      panelClass: ['learnspace-theme'],
      data
    }).afterClosed().pipe(filter(val => val === 'ok')).subscribe(action);
  }

  // Placeholder: destination/behavior not yet defined by product, no data source available on Learnspace/Course today.
  openCustomLink() { }

  // Placeholder: reuse action not yet defined by product (could map to CoursesAPIService.duplicateLearnspace).
  reuseProgram() { }

  private openURL(url: string) {
    (window.top || window).location.assign(url);
  }

  private _updateDialogSize() {
    const isMobile = window.innerWidth < 600; // 600px = media-breakpoint-down('xs')
    // Mobile: quasi full-screen (10px margin), matching the player's own
    // #learner-report-container sizing and the Figma mobile spec.
    this.dialogRef.updateSize(
      isMobile ? 'calc(100vw - 20px)' : 'min(80vw, 1200px)',
      isMobile ? 'calc(100dvh - 20px)' : 'min(90vh, 800px)'
    );
  }

  ngOnDestroy() {
    this._resizeSub?.unsubscribe();
  }
}
